import { Button, TableItem } from 'components/atoms';
import LabelBoolen from 'components/molecules/labeledStatus/LabeledBoolen';
import LabelSignatureStatus from 'components/molecules/labeledStatus/LabeledSignature';
import { Table } from 'components/organisms';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTranslation } from 'react-i18next';
import { PiCaretLeftBold } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetHistoricalAdvances } from 'services/advance';
import { REPAYMENTS_TYPE, SIGNATURE_TYPE } from 'services/advance/types';
import { ILabelForm } from 'services/labels/types';
import { formatCurrency } from 'utils/global/currency.utils';
import { formatDate } from 'utils/global/date.utils';
import { getMaxFormattedLength } from 'utils/global/getMaxFormattedLength.utils';

const LabelAdvanceHistory = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  usePermissionGuard('AdvancePermissionsLIST**');

  const { status }: IFormikContextType<ILabelForm> =
    useFormikContext<ILabelForm>();

  const id = useParamId();

  const historicalAdvances = useGetHistoricalAdvances(id);

  const tableProps = useInfiniteTable(historicalAdvances);

  const amountLength = getMaxFormattedLength(
    tableProps.data?.nodes ?? [],
    e => e?.amount
  );

  const remainingNetAmountLength = getMaxFormattedLength(
    tableProps.data?.nodes ?? [],
    e => e?.remainingNetAmount
  );

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t('history.advance')}
          size='compact'
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() => navigate(`/label/${id}/advance`)}>
              <Button.Icon icon={<PiCaretLeftBold />} />
            </Button>
          }
        />
      }>
      <Table
        id={'advanceLabelHistory'}
        {...tableProps}
        data={{
          nodes: tableProps.data.nodes.map(d => ({
            ...d,
            id: `/label/${id}/advance/${d.id}/repayments`,
          })),
        }}
        size='compact'
        displayMode
        columns={[
          {
            label: t('input.label.date'),
            renderCell: item => (
              <TableItem>
                <TableItem.Text text={formatDate(item.date)} />
              </TableItem>
            ),
            resize: true,
            footer: t('table.total'),
          },
          {
            label: t('input.label.description'),
            renderCell: item => (
              <TableItem>
                <TableItem.Text text={`${item.description}`} />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.signature'),
            renderCell: item => (
              <TableItem>
                <LabelSignatureStatus
                  status={
                    item.forSignature
                      ? SIGNATURE_TYPE.true
                      : SIGNATURE_TYPE.false
                  }
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.amount'),
            renderCell: item => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(item.amount, {
                    length: amountLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.remainingNetAmount'),
            renderCell: item => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(item.remainingNetAmount, {
                    length: remainingNetAmountLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.repaymentsFromAllContracts'),
            renderCell: (item: any) => (
              <TableItem>
                <LabelBoolen
                  status={
                    item.repaymentsFromAllContracts
                      ? REPAYMENTS_TYPE.true
                      : REPAYMENTS_TYPE.false
                  }
                />
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default LabelAdvanceHistory;
